import { useMemo, useState } from "react";
import "./taskList.scss";
import { SvgImageSwap } from "../../display-items/SVGIcon";
import ArrowIcon from "@icons/ArrowIcon.svg?react";
import "../../../pageStructures/header/header.scss";
import PlusIcon from "@icons/PlusIcon.svg?react";
import TaskItemDemo, { DemoTask, getTaskPriorityValue } from "./TaskItemDemo";
import HorizontalCalendarDemo, {
  DemoCalendarBlock,
} from "./HorizontalCalendarDemo";
import CardStackCalendarDemo from "./CardStackCalendarDemo";
import ClockDurationIcon from "../../display-items/displayIcons/ClockDurationIcon";

/**
 * Fully self-contained showcase version of TaskListFilterable.
 * No redux store, router, i18n or network access - everything lives in
 * component state so it can be dropped into an iframe on the marketing site.
 * Tasks can be added, finished and removed, but not opened for editing.
 */

let demoIdCounter = 0;
const nextDemoId = () => `demo-task-${++demoIdCounter}`;

const todayAt = (hours: number, minutes = 0) => {
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

const isToday = (date: Date) =>
  date.toDateString() === new Date().toDateString();

const initialTasks: DemoTask[] = [
  {
    id: nextDemoId(),
    title: "Fix production login bug",
    urgency: 90,
    importance: 85,
  },
  {
    id: nextDemoId(),
    title: "Send client contract before deadline",
    urgency: 80,
    importance: 70,
  },
  {
    id: nextDemoId(),
    title: "Plan Q3 product roadmap",
    urgency: 30,
    importance: 85,
  },
  { id: nextDemoId(), title: "Review team OKRs", urgency: 20, importance: 65 },
  {
    id: nextDemoId(),
    title: "Answer routine support emails",
    urgency: 70,
    importance: 20,
  },
  {
    id: nextDemoId(),
    title: "Schedule recurring team standup",
    urgency: 60,
    importance: 30,
  },
  {
    id: nextDemoId(),
    title: "Reorganize old note archive",
    urgency: 15,
    importance: 10,
  },
  {
    id: nextDemoId(),
    title: "Browse industry newsletter",
    urgency: 10,
    importance: 15,
  },
  {
    id: nextDemoId(),
    title: "Daily standup prep",
    urgency: 65,
    importance: 55,
    scheduleStart: todayAt(9),
    scheduleEnd: todayAt(9, 30),
  },
  {
    id: nextDemoId(),
    title: "Call supplier about delivery",
    urgency: 75,
    importance: 60,
    scheduleStart: todayAt(11),
    scheduleEnd: todayAt(11, 45),
  },
  {
    id: nextDemoId(),
    title: "Wrap up weekly report",
    urgency: 55,
    importance: 70,
    scheduleStart: todayAt(15),
    scheduleEnd: todayAt(16),
  },
];

// Calendar events that are not tied to a task - meetings, calls, etc.
const todayEvents: DemoCalendarBlock[] = [
  {
    id: "event-1",
    title: "Team sync",
    start: todayAt(9, 30),
    end: todayAt(10),
  },
  {
    id: "event-2",
    title: "Client onboarding call",
    start: todayAt(13),
    end: todayAt(14),
  },
];

type FilterKey = "all" | "must" | "q2" | "q3" | "q4" | "today";

const FILTERS: {
  key: FilterKey;
  label: string;
  activeClass: string;
  borderClass: string;
}[] = [
  {
    key: "all",
    label: "All",
    activeClass: "active bg_brown300",
    borderClass: "border border-brown bg_brown150",
  },
  {
    key: "must",
    label: "Critical",
    activeClass: " bg_red500",
    borderClass: "border border-red bg_red150",
  },
  {
    key: "q2",
    label: "Strategic",
    activeClass: " bg_blue500",
    borderClass: "border border-blue bg_blue150",
  },
  {
    key: "q3",
    label: "Busywork",
    activeClass: " bg_orange500",
    borderClass: "border border-orange bg_orange150",
  },
  {
    key: "q4",
    label: "Distraction",
    activeClass: " bg_green500",
    borderClass: "border border-green bg_green150",
  },
];

function TaskListFilterableDemo() {
  const [tasks, setTasks] = useState<DemoTask[]>(initialTasks);
  const [finishedTasks, setFinishedTasks] = useState<DemoTask[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<FilterKey>("all");
  const [newUrgency, setNewUrgency] = useState(50);
  const [newImportance, setNewImportance] = useState(50);

  const {
    mustTasks,
    q2tasks,
    q3tasks,
    q4tasks,
    sortedVisibleTasks,
    todayTasks,
  } = useMemo(() => {
    const mustTasks = tasks.filter((t) => t.urgency > 50 && t.importance > 50);
    const q2tasks = tasks.filter((t) => t.urgency <= 50 && t.importance >= 50);
    const q3tasks = tasks.filter((t) => t.importance < 50 && t.urgency > 50);
    const q4tasks = tasks.filter((t) => t.importance < 50 && t.urgency <= 50);
    const sortedVisibleTasks = [...tasks].sort(
      (a, b) => getTaskPriorityValue(b) - getTaskPriorityValue(a),
    );
    const todayTasks = sortedVisibleTasks.filter(
      (t) => t.scheduleStart && isToday(t.scheduleStart),
    );
    return {
      mustTasks,
      q2tasks,
      q3tasks,
      q4tasks,
      sortedVisibleTasks,
      todayTasks,
    };
  }, [tasks]);

  const filteredTasks = (() => {
    switch (selectedFilter) {
      case "must":
        return mustTasks;
      case "q2":
        return q2tasks;
      case "q3":
        return q3tasks;
      case "q4":
        return q4tasks;
      case "today":
        return todayTasks;
      case "all":
      default:
        return sortedVisibleTasks;
    }
  })();

  const filterCount = (key: FilterKey) => {
    switch (key) {
      case "must":
        return mustTasks.length;
      case "q2":
        return q2tasks.length;
      case "q3":
        return q3tasks.length;
      case "q4":
        return q4tasks.length;
      case "today":
        return todayTasks.length;
      default:
        return sortedVisibleTasks.length;
    }
  };

  const scheduledEventMinutes = todayEvents.reduce(
    (acc, event) => acc + (event.end.getTime() - event.start.getTime()) / 60000,
    0,
  );

  const scheduledTaskMinutes = todayTasks.reduce(
    (acc, task) =>
      acc +
      (task.scheduleStart && task.scheduleEnd
        ? (task.scheduleEnd.getTime() - task.scheduleStart.getTime()) / 60000
        : 0),
    0,
  );

  const todayCalendarBlocks: DemoCalendarBlock[] = [
    ...todayEvents,
    ...todayTasks
      .filter((t) => t.scheduleStart && t.scheduleEnd)
      .map((t) => ({
        id: t.id,
        title: t.title,
        start: t.scheduleStart!,
        end: t.scheduleEnd!,
      })),
  ];

  const onFinishTask = (id: string) => {
    setTasks((prev) => {
      const task = prev.find((t) => t.id === id);
      if (task) setFinishedTasks((done) => [task, ...done]);
      return prev.filter((t) => t.id !== id);
    });
  };

  const onDeleteTask = (id: string) => {
    setTasks((prev) => prev.filter((t) => t.id !== id));
  };

  const onAddTask = () => {
    setTasks((prev) => [
      ...prev,
      {
        id: nextDemoId(),
        title: "New Task",
        urgency: newUrgency,
        importance: newImportance,
      },
    ]);
    setNewUrgency(50);
    setNewImportance(50);
  };

  return (
    <>
      <div className="task-list-group-filter">
        <button
          id="today-selection"
          className={`task-list-group-filter__button ${
            selectedFilter === "today"
              ? " "
              : todayTasks.length > 0
                ? "border border-brown"
                : "bg_grey200"
          }`}
          onClick={() => setSelectedFilter("today")}
        >
          <p className="p_small p_alt">Today</p>
        </button>
        {FILTERS.map(({ key, label, activeClass, borderClass }) => (
          <button
            key={key}
            className={`task-list-group-filter__button ${
              selectedFilter === key
                ? activeClass
                : filterCount(key) > 0
                  ? borderClass
                  : "bg_grey200"
            }`}
            onClick={() => setSelectedFilter(key)}
          >
            <p
              className={`p_small p_alt ${selectedFilter === key ? "inverted" : ""}`}
            >
              {label}
            </p>
          </button>
        ))}
      </div>
      <div className="task-list-group">
        <div className="kanban-container">
          <div
            className="flex column rounded"
            style={{ alignItems: "flex-start" }}
          >
            {selectedFilter === "today" && (
              <>
                {scheduledEventMinutes > 0 && (
                  <div
                    className="flex justify-space-between padding-15 bg_blue150 rounded-15"
                    style={{
                      height: 52,
                      width: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    <p>
                      <b>Scheduled events today</b>
                    </p>
                    <ClockDurationIcon
                      minutes={scheduledEventMinutes}
                      color="blue"
                    />
                  </div>
                )}

                <HorizontalCalendarDemo elements={todayCalendarBlocks} />
                <CardStackCalendarDemo elements={todayCalendarBlocks} />

                {scheduledTaskMinutes > 0 && (
                  <div
                    className="flex justify-space-between padding-15 bg_brown150 rounded-15"
                    style={{
                      height: 52,
                      width: "100%",
                      boxSizing: "border-box",
                    }}
                  >
                    <p>
                      <b>Scheduled tasks today</b>
                    </p>
                    <ClockDurationIcon minutes={scheduledTaskMinutes} />
                  </div>
                )}
              </>
            )}

            {filteredTasks.map((task) => (
              <TaskItemDemo
                key={task.id}
                task={task}
                onFinish={onFinishTask}
                onDelete={onDeleteTask}
                showSchedule={selectedFilter === "today"}
              />
            ))}
          </div>
        </div>

        {finishedTasks.length > 0 && (
          <div className="kanban-container">
            <div className="kanban-container__header with-content">
              <h3 className="p">Finished</h3>
            </div>
            {finishedTasks.map((task) => (
              <div className="task-list-item__element finished" key={task.id}>
                <div
                  className="task-list-item__container"
                  style={{ cursor: "default" }}
                >
                  <div className="task-list-item__wrapper">
                    <div className="task-list-item__content">
                      <h3 className="task-list-item__heading finished">
                        {task.title}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {filteredTasks.length === 0 && (
        <div className="empty-state-hint flex pulsate-fwd center">
          <h1>Add your first task</h1>
          <SvgImageSwap src={ArrowIcon} className="icon__big rotated-90" />
        </div>
      )}
      <header className={`header header-bottom`}>
        <div className={`header__container `}>
          <button
            className={`iconButton__alt big  bg_brown300`}
            onClick={onAddTask}
          >
            <SvgImageSwap
              src={PlusIcon}
              alt=""
              className={`icon filter_grey_700`}
              style={{ zIndex: 5 }}
            />
          </button>
        </div>
      </header>
    </>
  );
}

export default TaskListFilterableDemo;
